import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CustomInstructionValue } from '@components/forms/custom-instructions-form/custom-instructions-form.component';
import { SourcesButtonComponent } from '@components/sources/sources-button/sources-button.component';
import { Idea, IdeaInputParams } from '@models/Sessions/Idea/Idea';
import {
  ScriptEvidence,
  UltimateGenerateRequestPromptLanguageEnum,
} from '@models/Sessions/Idea/podcast-api';
import { Session } from '@models/Sessions/Session';
import { BusService } from '@services/bus.service';
import { EventsService } from '@services/events.service';
import { PodcastService } from '@services/sessions/podcast.service';
import { MessageService } from 'primeng/api';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { map, mergeMap, Observable } from 'rxjs';

import { PromptLanguagePickerComponent } from '../components/prompt-language-picker/prompt-language-picker.component';
import { sourcesValidator } from '../validators/sources.validator';
import { BrainstormConceptsComponent } from './brainstorm-concepts/brainstorm-concepts.component';
import {
  PodcastSettingsComponent,
  PodcastSettingsData,
} from './podcast-settings-dialog-content/podcast-settings-dialog-content.component';

interface Form {
  title: FormControl<string>;
  subject: FormControl<string>;
  sources: FormControl<string[]>;
  customInstruction: FormControl<CustomInstructionValue>;
  guests: FormControl<number>;
  sections: FormControl<number>;
  promptLanguage: FormControl<UltimateGenerateRequestPromptLanguageEnum>;
}

@Component({
  selector: 'app-new-podcast',
  standalone: true,
  imports: [
    CommonModule,
    ProgressBarModule,
    ButtonModule,
    SelectButtonModule,
    ReactiveFormsModule,
    AutoFocusModule,
    InputTextModule,
    CardModule,
    SkeletonModule,
    // components
    BrainstormConceptsComponent,
    PromptLanguagePickerComponent,
    SourcesButtonComponent,
  ],
  providers: [DialogService],
  templateUrl: './new-podcast.component.html',
  styleUrl: './new-podcast.component.css',
})
export class NewPodcastComponent implements OnInit {
  isGenerating = signal(false);
  form!: FormGroup<Form>;
  dialogRef: DynamicDialogRef | undefined;
  maxSourceItems = 5;

  @Input() topic: Session;
  @ViewChild(BrainstormConceptsComponent)
  brainstormConceptsComponent: BrainstormConceptsComponent;

  constructor(
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private busService: BusService,
    private eventsService: EventsService,
    private destroyRef: DestroyRef,
    private podcastService: PodcastService,
    private messageService: MessageService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    this.form = this.formBuilder.group({
      title: this.formBuilder.control<string>('', [Validators.required]),
      customInstruction: this.formBuilder.control<CustomInstructionValue>({
        name: '',
        content: '',
      }),
      subject: this.formBuilder.control<string>('', [Validators.required]),
      sources: this.formBuilder.control<string[]>(
        [],
        sourcesValidator({ maxLength: 5 }),
      ),
      guests: this.formBuilder.control(1, [Validators.required]),
      sections: this.formBuilder.control(6, [Validators.required]),
      promptLanguage:
        this.formBuilder.control<UltimateGenerateRequestPromptLanguageEnum>(
          'de',
        ),
    });
  }

  onPromptLanguageChange(lang: UltimateGenerateRequestPromptLanguageEnum) {
    this.form.patchValue({
      promptLanguage: lang,
    });
  }

  onSourcesChange(sources: string[]) {
    this.form.patchValue({
      sources,
    });
  }

  handleSettingsClick() {
    const { customInstruction, guests, sections } = this.form.value;

    this.dialogRef = this.dialogService.open(PodcastSettingsComponent, {
      modal: true,
      closable: true,
      contentStyle: { overflow: 'unset', width: '45rem' },
      header: 'Podcast Settings',
      closeOnEscape: true,
      dismissableMask: true,
      data: {
        customInstruction,
        guests,
        sections,
      } satisfies PodcastSettingsData,
    });

    this.dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings: PodcastSettingsData) => {
        if (!settings) return;
        this.form.patchValue(settings);
      });
  }

  goToScript() {
    this.messageService.clear();
    this.isGenerating.set(true);

    this.generate()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(data =>
          this.save(data).pipe(
            map((idea): [Idea, string] => [idea, data.inconsistency]),
          ),
        ),
      )
      .subscribe({
        next: ([createdIdea, inconsistency]) => {
          this.isGenerating.set(false);
          this.router.navigate(
            ['topics', this.topic._id, 'ideas', createdIdea._id],
            {
              queryParams: { edit: true },
              state: { inconsistency },
            },
          );
        },
        error: () => {
          this.isGenerating.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Something went wrong.',
            detail: 'Please try change input data or try later.',
            sticky: true,
          });
        },
      });
  }

  private generate(): Observable<{
    inputParams: IdeaInputParams;
    script: string;
    shortTitle?: string;
    oneLiner?: string;
    evidence: ScriptEvidence[];
    inconsistency?: string;
  }> {
    const concepts =
      this.brainstormConceptsComponent.form.get('concepts').value;
    const {
      title,
      subject,
      customInstruction,
      guests: numberOfGuests,
      sections: numberOfSections,
      promptLanguage,
      sources,
    } = this.form.getRawValue();

    const context = customInstruction.content;

    return this.podcastService
      .ultimateGenerate({
        document_type: 'podcast',
        topic: subject,
        context,
        number_of_sections: numberOfSections,
        number_of_guests: numberOfGuests,
        podcast_name: title,
        prompt_language: promptLanguage,
        sources,
      })
      .pipe(
        map(data => ({
          inputParams: {
            context,
            promptLanguage,
            guests: numberOfGuests,
            sections: numberOfSections,
            subject,
            concept: concepts.length
              ? concepts.filter(v => v.selected).map(v => v.concept)
              : undefined,
            includeResearch: true,
          },
          script: data.main_text,
          shortTitle: data.short_title,
          oneLiner: data.one_liner,
          evidence: data.evidence_info,
        })),
      );
  }

  private getTitle(shortTitle?: string) {
    return (
      shortTitle ||
      this.form.get('title').value ||
      this.form.get('subject').value ||
      ''
    ).substring(0, 45);
  }

  private save(
    data: {
      inputParams: IdeaInputParams;
      script: string;
      shortTitle?: string;
      oneLiner?: string;
    } & Partial<Pick<Idea, 'outline' | 'evidence'>>,
  ) {
    this.busService.publish(
      this.eventsService.requested.action.session.idea.create,
      {
        title: this.getTitle(data.shortTitle),
        one_liner: data.oneLiner,
        inputParams: data.inputParams,
        categories: [this.form.get('subject').value],
        outline: data.outline,
        textScript: { original: { value: data.script, v: 0 } },
        evidence: data.evidence,
        published: true,
      },
      this.topic,
    );

    return new Observable<Idea>(observer => {
      this.busService.subscribe(
        this.eventsService.received.action.session.idea.create.success,
        (idea: Idea) => {
          observer.next(idea);
          observer.complete();
        },
      );
    });
  }
}
